import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, type User } from "../api";
import { isElectronDesktop } from "../desktop";

interface AuthState {
  user: User | null;
  assignedKeys: string[];
  setAssignedKeys: (keys: string[]) => void;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Full session probe — clears user on auth failure. */
  refresh: () => Promise<void>;
  /**
   * Keep-alive for long-lived admin/Station sessions.
   * Only clears the user on explicit 401/403; network blips are ignored.
   */
  keepAlive: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [assignedKeys, setAssignedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api<{ user: User; assignedKeys: string[] }>("/api/auth/me");
      setUser(data.user);
      setAssignedKeys(data.assignedKeys);
      if (isElectronDesktop()) {
        void window.brexDesktop?.startSync?.();
      }
    } catch {
      setUser(null);
      setAssignedKeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const keepAlive = useCallback(async () => {
    try {
      const data = await api<{ user: User; assignedKeys: string[] }>("/api/auth/me");
      setUser(data.user);
      setAssignedKeys(data.assignedKeys);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setUser(null);
        setAssignedKeys([]);
      }
      // Network / 5xx: leave session as-is so Station stays online.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    const data = await api<{ user: User; assignedKeys: string[] }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    setUser(data.user);
    setAssignedKeys(data.assignedKeys);
    if (isElectronDesktop()) {
      void window.brexDesktop?.startSync?.();
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } finally {
      if (isElectronDesktop()) {
        void window.brexDesktop?.stopSync?.();
      }
      setUser(null);
      setAssignedKeys([]);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      assignedKeys,
      setAssignedKeys,
      loading,
      login,
      logout,
      refresh,
      keepAlive,
    }),
    [user, assignedKeys, loading, login, logout, refresh, keepAlive]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
